import {
  IconListItem,
  toWSMoneyString,
  WSButton,
  WSButtons,
  WSContainer,
  WSFlexBox,
  WSGridCentered,
  WSIcon,
  WSIconList,
  WSLoader,
  WSPanel,
  WSProgressBar,
  WSText
} from "@wingspanhq/fe-component-library";
import {
  ClientWorkFlowStatus,
  IInvoiceLineItemsCreateRequest,
  InvoiceStatus,
  IPayableCreateRequest,
  IPayableSchema,
  IPayableUpdateRequest
} from "@wingspanhq/payments/dist/interfaces";
import { Currency } from "@wingspanhq/users/dist/lib/interfaces";
import { AxiosError } from "axios";
import qs from "qs";
import React, { useEffect, useState } from "react";
import { CSVLink } from "react-csv";
import { useHistory } from "react-router-dom";
import { useBrowserPageTitle } from "../../../../components/BrowserPageTitle/BrowserPageTitle";
import { PreventLeave } from "../../../../components/PreventLeave/PreventLeave";
import { ProjectOnboardingLayout } from "../../../../components/ProjectOnboardingLayout/ProjectOnboardingLayout";
import { useWSMutation } from "../../../../query/helpers";
import { useUserId } from "../../../../query/hooks/helpers";
import {
  QUERY_INVOICES,
  QUERY_PAYABLES
} from "../../../../query/payments/keys";
import { useCollaboratorsQuery } from "../../../../query/payments/queries";
import { getVisibleCollaboratorsSortByCreatedAt } from "../../../../query/payments/selectors";
import { InvoicesFilter, paymentsService } from "../../../../services/payments";
import { track } from "../../../../utils/analytics";
import { bulkActionFactory } from "../../../../utils/bulkActionFactory";
import { isSameDate } from "../../../../utils/dates";
import { pluralize } from "../../../../utils/stringHelper";
import { LINE_ITEM_HOUR_RATE } from "../../../utils/invoicesFormUtils";
import { getMatchingCollaborators } from "../../bulkUploadCollaborators/collaboratorUtils";
import { PayableStatus, UploadPayable } from "./BulkUploadPayablesInfo";

export interface BulkUploadPayablesReviewProps {}

interface PayableLineItem {
  title: string;
  description?: string;
  amount?: string;
  hours?: string;
  hourlyRate?: string;
  reimbursable?: boolean;
  labels?: { [key: string]: string };
}
interface MergedPayable {
  email?: string;
  collaboratorId?: string;
  contractorId?: string;
  lineItems: PayableLineItem[];
  dueDate: Date;
  invoiceNotes?: string;
  $custom?: { [key: string]: string };
}

type IdentifierKey = "email" | "collaboratorId" | "contractorId";

interface FailedPayable extends UploadPayable {
  error: string;
}

interface ToBeCreatedPayable {
  mergedPayable: MergedPayable;
  dbPayable?: IPayableSchema;
  requestPayload: IPayableCreateRequest;
}

interface ToBeUpdatedPayable {
  mergedPayable: MergedPayable;
  dbPayable: IPayableSchema;
  requestPayload: { id: string } & IPayableUpdateRequest;
}
interface ImportPreviewStats {
  totalLineItemsAmount: number;
  totalImportablePayables: number;
  collaboratorsCount: number;
  statusUpdateCount: number;
  negativeAmountCount: number;
  toBeCreatedPayables: Array<ToBeCreatedPayable>;
  toBeUpdatedPayables: Array<ToBeUpdatedPayable>;
}

interface CreatePayableBulkActionFactoryResult {
  successItems: ToBeCreatedPayable[];
  failedItems: ToBeCreatedPayable[];
}

interface UpdatePayableBulkActionFactoryResult {
  successItems: ToBeUpdatedPayable[];
  failedItems: ToBeUpdatedPayable[];
}

const csvLineItemToRequest = (
  csvLineItem: PayableLineItem
): IInvoiceLineItemsCreateRequest => {
  const isHourlyRateSet = !!csvLineItem.hourlyRate && !!csvLineItem.hours;

  return {
    description: csvLineItem.title,
    totalCost: isHourlyRateSet
      ? Number(csvLineItem.hourlyRate) * Number(csvLineItem.hours)
      : Number(csvLineItem.amount?.replaceAll(",", "")),
    // @ts-ignore
    detail: csvLineItem.description,
    unit: isHourlyRateSet ? LINE_ITEM_HOUR_RATE : undefined,
    costPerUnit: isHourlyRateSet ? Number(csvLineItem.hourlyRate) : undefined,
    quantity: isHourlyRateSet ? Number(csvLineItem.hours) : undefined,
    reimbursableExpense: csvLineItem.reimbursable,
    ...(csvLineItem.labels && { labels: csvLineItem.labels })
  };
};

export const BulkUploadPayablesReview: React.FC<BulkUploadPayablesReviewProps> = () => {
  useBrowserPageTitle("Wingspan - Bulk upload payables");
  const history = useHistory<{
    lineItems: UploadPayable[];
    fileName: string;
    batchId: string;
  }>();
  const {
    lineItems,
    fileName: uploadFileName,
    batchId
  } = history.location.state;

  const payableStatus: PayableStatus = qs.parse(
    history.location.search.substring(1)
  ).status as PayableStatus;

  const [importPreview, setImportPreview] = useState<ImportPreviewStats | null>(
    null
  );

  const [
    bulkCreatePayablesApiResult,
    setBulkCreatePayablesApiResult
  ] = useState<CreatePayableBulkActionFactoryResult | null>(null);
  const [
    bulkUpdatePayablesApiResult,
    setBulkUpdatePayablesApiResult
  ] = useState<UpdatePayableBulkActionFactoryResult | null>(null);

  // below two state vars are for displaying the progress bar
  const [createPayablesApiResults, setCreatePayablesApiResults] = useState<
    {
      error?: any;
      email?: string;
      contractorId?: string;
      collaboratorId?: string;
    }[]
  >([]);
  const [updatePayablesApiResults, setUpdatePayablesApiResults] = useState<
    {
      error?: any;
      email?: string;
      contractorId?: string;
      collaboratorId?: string;
    }[]
  >([]);

  // for downloadable csv including errors
  const [failedLineItems, setFailedLineItems] = useState<FailedPayable[]>([]);

  const userId = useUserId();
  const collaboratorsQuery = useCollaboratorsQuery();

  const existingCollaborators = getVisibleCollaboratorsSortByCreatedAt(
    collaboratorsQuery.data || []
  );

  const [confirmAndImport, confirmAndImportMeta] = useWSMutation<
    any,
    AxiosError,
    {
      toBeCreatedPayables: ToBeCreatedPayable[];
      toBeUpdatedPayables: ToBeUpdatedPayable[];
    }
  >(
    async ({ toBeCreatedPayables, toBeUpdatedPayables }) => {
      const toBeCreatedPayablesApiResult = await bulkCreatePayables.run(
        toBeCreatedPayables
      );
      const toBeUpdatedPayablesApiResult = await bulkUpdatePayables.run(
        toBeUpdatedPayables
      );
      track("Payable Bulk Upload Completed", {
        createdCount: toBeCreatedPayablesApiResult.successItems.length,
        updatedCount: toBeUpdatedPayablesApiResult.successItems.length,
        errorCount:
          toBeCreatedPayablesApiResult.failedItems.length +
          toBeUpdatedPayablesApiResult.failedItems.length,
        errors: [
          ...toBeCreatedPayablesApiResult.failedItems,
          ...toBeUpdatedPayablesApiResult.failedItems
        ].map(failedPayable => failedPayable.mergedPayable)
      });
    },
    { dependencies: [QUERY_PAYABLES, QUERY_INVOICES] }
  );

  const [retry, retryMeta] = useWSMutation<
    any,
    AxiosError,
    {
      failedCreatePayables: ToBeCreatedPayable[];
      failedUpdatePayables: ToBeUpdatedPayable[];
    }
  >(
    async ({ failedCreatePayables, failedUpdatePayables }) => {
      await bulkCreatePayables.run(failedCreatePayables);
      await bulkUpdatePayables.run(failedUpdatePayables);
    },
    { dependencies: [QUERY_PAYABLES, QUERY_INVOICES] }
  );

  useEffect(() => {
    const getImportPreview = async () => {
      const lineItems = history.location.state?.lineItems || [];
      if (lineItems?.length === 0) {
        history.replace("/member/invoices/payables/bulk-upload/info");
        return;
      }
      const importPreviewStats = await getLineItemsImportPreview();
      setImportPreview(importPreviewStats);
      track("Payable Bulk Upload Previewed", {
        amount: toWSMoneyString(importPreviewStats?.totalLineItemsAmount),
        collaboratorCount: importPreviewStats?.collaboratorsCount,
        createdCount: importPreviewStats?.toBeCreatedPayables.length,
        updatedCount: importPreviewStats?.toBeUpdatedPayables.length,
        openedCount:
          payableStatus === PayableStatus.Open
            ? importPreviewStats?.statusUpdateCount
            : 0,
        approvedCount:
          payableStatus === PayableStatus.Approved
            ? importPreviewStats?.statusUpdateCount
            : 0,
        draftCount: importPreviewStats?.negativeAmountCount
      });
    };
    getImportPreview();
  }, []);

  useEffect(() => {
    const unloadCallback = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
      return "";
    };

    window.addEventListener("beforeunload", unloadCallback);
    return () => window.removeEventListener("beforeunload", unloadCallback);
  }, []);

  const bulkCreatePayables = bulkActionFactory({
    action: (data: ToBeCreatedPayable) => createPayableWrapper(data),
    onFinish: result => {
      setBulkCreatePayablesApiResult(result);
    },
    concurrentLimit: 1 // API allows to create only 1 invoice/payable at time
  });

  const bulkUpdatePayables = bulkActionFactory({
    action: (data: ToBeUpdatedPayable) => updatePayableWrapper(data),
    onFinish: result => {
      setBulkUpdatePayablesApiResult(result);
    }
  });

  const prepareLineItemsFromPayable = (
    payable: MergedPayable
  ): UploadPayable[] => {
    return payable.lineItems.map(lineItem => ({
      email: payable.email,
      collaboratorId: payable.collaboratorId,
      contractorId: payable.contractorId,
      dueDate: payable.dueDate,
      invoiceNotes: payable.invoiceNotes,
      amount: lineItem.amount,
      lineItemTitle: lineItem.title,
      lineItemDescription: lineItem.description,
      lineItemHourlyRate: lineItem.hourlyRate,
      lineItemHours: lineItem.hours,
      reimbursable: lineItem.reimbursable
    }));
  };

  const getIdentifierKeyFromLineItems = (
    lineItems: UploadPayable[]
  ): IdentifierKey => {
    if (lineItems.every(li => !!li.email)) {
      return "email";
    } else if (lineItems.every(li => !!li.collaboratorId)) {
      return "collaboratorId";
    } else if (lineItems.every(li => !!li.contractorId)) {
      return "contractorId";
    }
    return "email";
  };

  const canMergeLineItem = (
    payables: MergedPayable[],
    lineItem: UploadPayable,
    identifierKey: IdentifierKey
  ) => {
    return (
      payables.filter(payable => {
        const isSameDueDate = isSameDate(
          new Date(payable?.dueDate),
          new Date(lineItem?.dueDate)
        );
        return (
          isSameDueDate && payable[identifierKey] === lineItem[identifierKey]
        );
      }).length > 0
    );
  };

  const preparePayablesFromLineItems = (
    lineItems: UploadPayable[]
  ): MergedPayable[] => {
    const payables: MergedPayable[] = [];
    const identifierKey = getIdentifierKeyFromLineItems(lineItems);
    lineItems.forEach(lineItem => {
      const canMerge = canMergeLineItem(payables, lineItem, identifierKey);
      const basePayable = {
        email: lineItem.email,
        collaboratorId: lineItem.collaboratorId,
        contractorId: lineItem.contractorId,
        dueDate: lineItem.dueDate,
        invoiceNotes: lineItem.invoiceNotes
      };
      const lineItemLabels = {
        ...(lineItem["$custom"] ? lineItem["$custom"] : {}),
        filename: uploadFileName,
        uploadBatchId: batchId,
        uploadedBy: userId,
        uploadedAt: new Date().toISOString()
      };

      if (canMerge) {
        const mergeablePayableIdx = payables.findIndex(payable => {
          const isSameDueDate = isSameDate(
            new Date(payable?.dueDate),
            new Date(lineItem?.dueDate)
          );
          return (
            isSameDueDate && payable[identifierKey] === lineItem[identifierKey]
          );
        });
        if (mergeablePayableIdx !== -1) {
          payables[mergeablePayableIdx] = {
            ...payables[mergeablePayableIdx],
            lineItems: [
              ...payables[mergeablePayableIdx].lineItems,
              {
                amount: lineItem.amount,
                description: lineItem.lineItemDescription,
                title: lineItem.lineItemTitle,
                hours: lineItem.lineItemHours,
                hourlyRate: lineItem.lineItemHourlyRate,
                reimbursable: lineItem.reimbursable,
                labels: lineItemLabels
              }
            ]
          };
        }
      } else {
        payables.push({
          ...basePayable,
          lineItems: [
            {
              title: lineItem.lineItemTitle,
              description: lineItem.lineItemDescription,
              amount: lineItem.amount,
              hours: lineItem.lineItemHours,
              hourlyRate: lineItem.lineItemHourlyRate,
              reimbursable: lineItem.reimbursable,
              labels: lineItemLabels
            }
          ]
        });
      }
    });
    return payables;
  };

  const createPayableWrapper = async ({
    mergedPayable: uploadPayable,
    requestPayload
  }: ToBeCreatedPayable) => {
    const dueDate = new Date();
    let dateShift = 5 - dueDate.getDay();
    dateShift = dateShift < 0 ? dateShift + 7 : dateShift;
    dueDate.setDate(dueDate.getDate() + dateShift);

    try {
      const csvPayableTotalAmount = uploadPayable.lineItems.reduce(
        (total, lineItem) =>
          total + Number(lineItem.amount?.replace(/[^0-9.-]+/g, "")),
        0
      );
      const response = await paymentsService.payable.create(requestPayload);
      if (
        [PayableStatus.Open, PayableStatus.Approved].includes(payableStatus) &&
        csvPayableTotalAmount > 0
      ) {
        await paymentsService.payable.update(response.payableId, {
          client:
            payableStatus === PayableStatus.Approved
              ? {
                  workflowStatus: ClientWorkFlowStatus.Approved
                }
              : undefined,
          status: InvoiceStatus.Open
        });
      }
      setCreatePayablesApiResults(prev => [
        ...prev,
        {
          ...(uploadPayable.email && { email: uploadPayable.email }),
          ...(uploadPayable.contractorId && {
            contractorId: uploadPayable.contractorId
          })
        }
      ]);
    } catch (error) {
      setCreatePayablesApiResults(prev => [
        ...prev,
        {
          ...(uploadPayable.email && { email: uploadPayable.email }),
          ...(uploadPayable.collaboratorId && {
            collaboratorId: uploadPayable.collaboratorId
          }),
          ...(uploadPayable.contractorId && {
            contractorId: uploadPayable.contractorId
          }),
          error: (error as AxiosError)?.response?.data.error
        }
      ]);
      const lineItems = prepareLineItemsFromPayable(uploadPayable);
      const lineItemsWithError = lineItems.map(li => ({
        ...li,
        error: (error as AxiosError)?.response?.data.error
      }));
      setFailedLineItems(failedLineItems => [
        ...failedLineItems,
        ...lineItemsWithError
      ]);
      throw error;
    }
  };

  const updatePayableWrapper = async ({
    mergedPayable: uploadPayable,
    requestPayload
  }: ToBeUpdatedPayable) => {
    try {
      await paymentsService.payable.update(requestPayload.id, requestPayload);
      setUpdatePayablesApiResults(prev => [
        ...prev,
        {
          ...(uploadPayable.email && { email: uploadPayable.email }),
          ...(uploadPayable.contractorId && {
            contractorId: uploadPayable.contractorId
          })
        }
      ]);
    } catch (error) {
      setUpdatePayablesApiResults(prev => [
        ...prev,
        {
          ...(uploadPayable.email && { email: uploadPayable.email }),
          ...(uploadPayable.collaboratorId && {
            collaboratorId: uploadPayable.collaboratorId
          }),
          ...(uploadPayable.contractorId && {
            contractorId: uploadPayable.contractorId
          }),
          error: (error as AxiosError)?.response?.data.error
        }
      ]);
      const lineItems = prepareLineItemsFromPayable(uploadPayable);
      const lineItemsWithError = lineItems.map(li => ({
        ...li,
        error: (error as AxiosError)?.response?.data.error
      }));
      setFailedLineItems(failedLineItems => [
        ...failedLineItems,
        ...lineItemsWithError
      ]);
      throw error;
    }
  };

  const getExistingPayablesForMember = async (payableData: MergedPayable) => {
    const matchingCollaborators = getMatchingCollaborators(
      existingCollaborators,
      {
        email: payableData.email,
        collaboratorId: payableData.collaboratorId,
        contractorId: payableData.contractorId
      }
    );
    const matchingCollaborator = matchingCollaborators[0];
    if (!matchingCollaborator) {
      // If the contractor is not on wingspan, don't do anything
      return;
    }
    const filter: InvoicesFilter = {
      memberClientId: {
        in: [matchingCollaborator.collaboratorId]
      },
      status: {
        in: [
          InvoiceStatus.Draft,
          InvoiceStatus.Pending,
          InvoiceStatus.Open,
          InvoiceStatus.Overdue
        ]
      },
      "client.workflowStatus": {
        "!=": ClientWorkFlowStatus.Funded
      }
    };
    const invoiceQuery = {
      filter
    };
    const existingPayablesForMember = await paymentsService.payable.list(
      invoiceQuery
    );
    return existingPayablesForMember.data;
  };

  const onConfirmAndImport = async () => {
    setCreatePayablesApiResults([]);
    setUpdatePayablesApiResults([]);

    confirmAndImport({
      toBeCreatedPayables: importPreview?.toBeCreatedPayables || [],
      toBeUpdatedPayables: importPreview?.toBeUpdatedPayables || []
    });
  };

  const onRetry = async () => {
    setCreatePayablesApiResults([]);
    setUpdatePayablesApiResults([]);

    setBulkCreatePayablesApiResult(null);
    setBulkUpdatePayablesApiResult(null);

    setFailedLineItems([]);

    const failedCreatePayables = bulkCreatePayablesApiResult?.failedItems || [];
    const failedUpdatePayables = bulkUpdatePayablesApiResult?.failedItems || [];
    retry({ failedCreatePayables, failedUpdatePayables });
  };

  const csvProps = {
    filename: `Wingspan_Failed_Bulk_Upload_Payables_${Date.now()}.csv`,
    headers: [
      { label: "Email", key: "email" },
      { label: "Collaborator ID", key: "collaboratorId" },
      { label: "External Contractor ID", key: "contractorId" },
      { label: "Amount (USD)", key: "amount" },
      { label: "Line Item Title", key: "lineItemTitle" },
      { label: "Line Item Description", key: "lineItemDescription" },
      { label: "Due Date", key: "dueDate" },
      { label: "Invoice Notes", key: "invoiceNotes" },
      { label: "Error", key: "error" }
    ],
    data: failedLineItems
  };

  const getTotalLineItemsAmountFromCSV = (
    csvLineItems: UploadPayable[] = lineItems
  ) => {
    return csvLineItems.reduce((total, csvLineItem) => {
      const isHourlyRateSet =
        !!csvLineItem.lineItemHourlyRate && !!csvLineItem.lineItemHours;
      const totalCost = isHourlyRateSet
        ? Number(csvLineItem.lineItemHourlyRate) *
          Number(csvLineItem.lineItemHours)
        : Number(csvLineItem.amount?.replaceAll(",", ""));

      return total + totalCost;
    }, 0);
  };

  const getUpdateLineItemsRequest = (
    dbPayable: IPayableSchema,
    csvPayable: MergedPayable
  ) => {
    let lineItemsUpdate = [];
    for (let i = 0; i < dbPayable.lineItems.length; i++) {
      lineItemsUpdate.push({});
    }
    lineItemsUpdate = [
      ...lineItemsUpdate,
      ...csvPayable.lineItems.map(csvLineItemToRequest)
    ];
    return lineItemsUpdate;
  };

  const getLineItemsImportPreview = async () => {
    const totalLineItemsAmount = getTotalLineItemsAmountFromCSV();
    const importablePayables = preparePayablesFromLineItems(lineItems);
    const identifierKey = getIdentifierKeyFromLineItems(lineItems);
    const collaborators = lineItems.map(lineItem => lineItem[identifierKey]);
    const uniqueCollaborators = [...new Set(collaborators)];
    let toBeCreatedPayables: Array<ToBeCreatedPayable> = [];
    let toBeUpdatedPayables: Array<ToBeUpdatedPayable> = [];

    const paymentsSettings = await paymentsService.service.get();

    for (const csvPayable of importablePayables) {
      const matchingCollaborators = getMatchingCollaborators(
        existingCollaborators,
        {
          email: csvPayable.email,
          collaboratorId: csvPayable.collaboratorId,
          contractorId: csvPayable.contractorId
        }
      );
      const matchingCollaborator = matchingCollaborators[0];

      if (!matchingCollaborator) {
        return null;
      }
      const existingPayablesForMember = (await getExistingPayablesForMember(
        csvPayable
      )) as IPayableSchema[];
      const matchedPayablesByCollaboratorIdAndDueDate = existingPayablesForMember.filter(
        dbPayable =>
          isSameDate(new Date(dbPayable.dueDate), new Date(csvPayable.dueDate))
      );

      const createPayableRequest: IPayableCreateRequest = {
        collaboratorId: matchingCollaborator.collaboratorId,
        lineItems: csvPayable.lineItems.map(csvLineItemToRequest),
        dueDate: csvPayable.dueDate,
        creditFeeHandling: {
          clientPays: 100,
          memberPays: 0
        },
        currency: paymentsSettings.defaultAccountCurrency || Currency.USD,
        invoiceNotes: csvPayable.invoiceNotes
      };
      const csvPayableTotalAmount = csvPayable.lineItems.reduce(
        (total, lineItem) =>
          total + Number(lineItem.amount?.replace(/[^0-9.-]+/g, "")),
        0
      );
      const isPayableAlreadyExists =
        matchedPayablesByCollaboratorIdAndDueDate.length > 0;

      const updateAtSortComparator = (
        a: { updatedAt: Date },
        b: { updatedAt: Date }
      ) => b.updatedAt.getTime() - a.updatedAt.getTime();
      if (isPayableAlreadyExists) {
        if (payableStatus === PayableStatus.Draft) {
          const matchingDraftPayables = matchedPayablesByCollaboratorIdAndDueDate.filter(
            dbPayable =>
              dbPayable.status === InvoiceStatus.Draft &&
              (dbPayable.client.workflowStatus ===
                ClientWorkFlowStatus.Pending ||
                !Object(dbPayable.client).hasOwnProperty("workflowStatus"))
          );
          if (matchingDraftPayables.length > 0) {
            const latestMatchingDraftPayable = matchingDraftPayables.sort(
              updateAtSortComparator
            )[0];
            const updatePayableRequest = {
              id: latestMatchingDraftPayable.payableId,
              lineItems: getUpdateLineItemsRequest(
                latestMatchingDraftPayable,
                csvPayable
              ),
              invoiceNotes:
                csvPayable.invoiceNotes ??
                latestMatchingDraftPayable.invoiceNotes
            };
            toBeUpdatedPayables.push({
              mergedPayable: csvPayable,
              dbPayable: latestMatchingDraftPayable,
              requestPayload: updatePayableRequest
            });
          } else {
            toBeCreatedPayables.push({
              mergedPayable: csvPayable,
              requestPayload: {
                ...createPayableRequest,
                status: InvoiceStatus.Draft
              }
            });
          }
        } else if (payableStatus === PayableStatus.Open) {
          const matchingOpenPayables = matchedPayablesByCollaboratorIdAndDueDate.filter(
            dbPayable =>
              [
                InvoiceStatus.Pending,
                InvoiceStatus.Draft,
                InvoiceStatus.Open
              ].includes(dbPayable.status) &&
              (dbPayable.client.workflowStatus ===
                ClientWorkFlowStatus.Pending ||
                !Object(dbPayable.client).hasOwnProperty("workflowStatus"))
          );
          if (matchingOpenPayables.length > 0) {
            const latestMatchingOpenPayable = matchingOpenPayables.sort(
              updateAtSortComparator
            )[0];
            const totalPayableAmount =
              latestMatchingOpenPayable.amount + csvPayableTotalAmount;
            if (
              totalPayableAmount > 0 ||
              (totalPayableAmount < 0 && latestMatchingOpenPayable.amount < 0)
            ) {
              const updatePayableRequest = {
                id: latestMatchingOpenPayable.payableId,
                lineItems: getUpdateLineItemsRequest(
                  latestMatchingOpenPayable,
                  csvPayable
                ),
                invoiceNotes:
                  csvPayable.invoiceNotes ??
                  latestMatchingOpenPayable.invoiceNotes,
                status: [InvoiceStatus.Pending, InvoiceStatus.Draft].includes(
                  latestMatchingOpenPayable.status
                )
                  ? totalPayableAmount < 0 &&
                    latestMatchingOpenPayable.amount < 0
                    ? InvoiceStatus.Draft
                    : InvoiceStatus.Open
                  : latestMatchingOpenPayable.status
              };
              toBeUpdatedPayables.push({
                mergedPayable: csvPayable,
                dbPayable: latestMatchingOpenPayable,
                requestPayload: updatePayableRequest
              });
            } else {
              toBeCreatedPayables.push({
                mergedPayable: csvPayable,
                dbPayable: latestMatchingOpenPayable,
                requestPayload: {
                  ...createPayableRequest,
                  status: InvoiceStatus.Draft
                }
              });
            }
          } else {
            toBeCreatedPayables.push({
              mergedPayable: csvPayable,
              requestPayload: createPayableRequest
            });
          }
        } else if (payableStatus === PayableStatus.Approved) {
          const matchingApprovedPayables = matchedPayablesByCollaboratorIdAndDueDate.filter(
            dbPayable =>
              [
                InvoiceStatus.Pending,
                InvoiceStatus.Draft,
                InvoiceStatus.Open
              ].includes(dbPayable.status) &&
              (dbPayable.client.workflowStatus ===
                ClientWorkFlowStatus.Approved ||
                !Object(dbPayable.client).hasOwnProperty("workflowStatus"))
          );
          if (matchingApprovedPayables.length > 0) {
            const latestMatchingApprovedPayable = matchingApprovedPayables.sort(
              updateAtSortComparator
            )[0];
            const totalPayableAmount =
              latestMatchingApprovedPayable.amount + csvPayableTotalAmount;
            if (
              totalPayableAmount > 0 ||
              (totalPayableAmount < 0 &&
                latestMatchingApprovedPayable.amount < 0)
            ) {
              const updatePayableRequest = {
                id: latestMatchingApprovedPayable.payableId,
                lineItems: getUpdateLineItemsRequest(
                  latestMatchingApprovedPayable,
                  csvPayable
                ),
                invoiceNotes:
                  csvPayable.invoiceNotes ??
                  latestMatchingApprovedPayable.invoiceNotes,
                status: [InvoiceStatus.Pending, InvoiceStatus.Draft].includes(
                  latestMatchingApprovedPayable.status
                )
                  ? totalPayableAmount < 0 &&
                    latestMatchingApprovedPayable.amount < 0
                    ? InvoiceStatus.Draft
                    : InvoiceStatus.Open
                  : latestMatchingApprovedPayable.status,
                client: {
                  workflowStatus: ClientWorkFlowStatus.Approved
                }
              };
              toBeUpdatedPayables.push({
                mergedPayable: csvPayable,
                dbPayable: latestMatchingApprovedPayable,
                requestPayload: updatePayableRequest
              });
            } else {
              toBeCreatedPayables.push({
                mergedPayable: csvPayable,
                dbPayable: latestMatchingApprovedPayable,
                requestPayload: {
                  ...createPayableRequest,
                  status: InvoiceStatus.Draft
                }
              });
            }
          } else {
            toBeCreatedPayables.push({
              mergedPayable: csvPayable,
              requestPayload: {
                ...createPayableRequest,
                status: InvoiceStatus.Draft
              }
            });
          }
        }
      } else {
        toBeCreatedPayables.push({
          mergedPayable: csvPayable,
          requestPayload: {
            ...createPayableRequest,
            status: InvoiceStatus.Draft
          }
        });
      }
    }

    let statusUpdateCount = 0;
    let negativeAmountCount = 0;
    if (payableStatus === PayableStatus.Open) {
      statusUpdateCount = toBeUpdatedPayables.filter(
        ({ dbPayable, requestPayload }) =>
          dbPayable.status === InvoiceStatus.Draft &&
          requestPayload.status === InvoiceStatus.Open
      ).length;
    } else if (payableStatus === PayableStatus.Approved) {
      statusUpdateCount = toBeUpdatedPayables.filter(
        ({ dbPayable, requestPayload }) =>
          [
            InvoiceStatus.Pending,
            InvoiceStatus.Draft,
            InvoiceStatus.Open
          ].includes(dbPayable.status) &&
          requestPayload.client?.workflowStatus ===
            ClientWorkFlowStatus.Approved
      ).length;
    }

    negativeAmountCount = toBeCreatedPayables.filter(({ mergedPayable }) => {
      const totalAmount = mergedPayable.lineItems.reduce(
        (total, lineItem) =>
          total + Number(lineItem.amount?.replace(/[^0-9.-]+/g, "")),
        0
      );
      return totalAmount < 0;
    }).length;

    return {
      totalLineItemsAmount,
      collaboratorsCount: uniqueCollaborators.length,
      totalImportablePayables: importablePayables.length,
      toBeCreatedPayables,
      toBeUpdatedPayables,
      statusUpdateCount,
      negativeAmountCount
    };
  };

  return (
    <ProjectOnboardingLayout progress={(100 / 3) * 3}>
      <WSContainer verticalPadding>
        <WSGridCentered M={8} S={6} XS={6}>
          <WSText.Label color="grey600" mt="S" mb="M" pt="S">
            3 of 3
          </WSText.Label>
          <WSText.Heading2>Review & upload payables</WSText.Heading2>
          <WSPanel mt="L">
            {(function() {
              const totalUploadingPayables =
                (importPreview?.toBeCreatedPayables || []).length +
                (importPreview?.toBeUpdatedPayables || []).length;

              const totalLineItemsAmount = toWSMoneyString(
                importPreview?.totalLineItemsAmount
              );

              const payablesPluralizeOptions = {
                one: "payable",
                other: "payables"
              };

              const collaboratorsPluralizeOptions = {
                one: "collaborator",
                other: "collaborators"
              };

              const payableStatusLabelMap = {
                [PayableStatus.Open]: "opened",
                [PayableStatus.Approved]: "approved"
              };
              const isUploading =
                confirmAndImportMeta.isLoading || retryMeta.isLoading;

              if (!importPreview) {
                return <WSLoader.Spinner />;
              } else if (isUploading) {
                const resultsLength =
                  createPayablesApiResults.length +
                  updatePayablesApiResults.length;
                const importPercent =
                  (resultsLength * 100) / totalUploadingPayables;
                return (
                  <>
                    <WSText.Heading3 mb="M">
                      Your information upload summary
                    </WSText.Heading3>
                    <PreventLeave
                      isEnabled={isUploading}
                      promptMessage="Your payables have already been uploaded and are in the process of being imported. Cancelling now can cause data issues. Are you sure you want to leave the page?"
                    />

                    <WSProgressBar
                      mt="L"
                      percent={importPercent || 0}
                      text={`Uploading: ${resultsLength} of ${totalUploadingPayables} payables...`}
                    />
                  </>
                );
              } else if (
                (retryMeta.isSuccess || confirmAndImportMeta.isSuccess) &&
                bulkCreatePayablesApiResult &&
                bulkUpdatePayablesApiResult
              ) {
                const failedUploadingPayablesApiResults = [
                  ...(bulkCreatePayablesApiResult?.failedItems as ToBeCreatedPayable[]),
                  ...(bulkUpdatePayablesApiResult?.failedItems as ToBeUpdatedPayable[])
                ];
                const successfulUploadedCount =
                  bulkCreatePayablesApiResult?.successItems.length +
                  bulkUpdatePayablesApiResult?.successItems.length;
                return (
                  <>
                    <WSText.Heading3 mb="M">
                      Your information upload summary
                    </WSText.Heading3>
                    <WSFlexBox.CenterY mb="M">
                      {failedUploadingPayablesApiResults.length > 0 ? (
                        <>
                          <WSIcon name="warning" color="coral" mr="S" />
                          <WSText as="span">
                            {failedUploadingPayablesApiResults.length}/
                            {totalUploadingPayables} payables failed to upload.{" "}
                          </WSText>
                          <CSVLink {...csvProps}>
                            <WSButton.Link>
                              Download CSV including errors.
                            </WSButton.Link>
                          </CSVLink>
                        </>
                      ) : totalUploadingPayables === successfulUploadedCount ? (
                        <WSText>
                          {successfulUploadedCount}{" "}
                          {pluralize(
                            successfulUploadedCount,
                            payablesPluralizeOptions
                          )}{" "}
                          successfully uploaded.
                        </WSText>
                      ) : null}
                    </WSFlexBox.CenterY>
                    <WSButton.Link
                      mb="L"
                      onClick={() => {
                        history.push(
                          "/member/invoices/payables/bulk-upload/info"
                        );
                      }}
                    >
                      Restart upload payables
                    </WSButton.Link>
                    {failedUploadingPayablesApiResults.length > 0 ? (
                      <WSButtons forceFullWidth>
                        <WSButton fullWidth onClick={onRetry}>
                          Try again
                        </WSButton>
                        <WSButton.Secondary
                          fullWidth
                          onClick={() =>
                            history.push("/member/invoices/payables")
                          }
                        >
                          Go to payables
                        </WSButton.Secondary>
                      </WSButtons>
                    ) : (
                      <WSButton
                        fullWidth
                        onClick={() =>
                          history.push("/member/invoices/payables")
                        }
                      >
                        Go to payables
                      </WSButton>
                    )}
                  </>
                );
              }

              return (
                <>
                  <WSText.Heading3 mb="M">Import Preview</WSText.Heading3>
                  <WSIconList
                    mb="L"
                    items={[
                      {
                        description: `${totalLineItemsAmount} of payables to ${
                          importPreview?.collaboratorsCount
                        } ${pluralize(
                          importPreview?.collaboratorsCount,
                          collaboratorsPluralizeOptions
                        )} will be imported.`,
                        icon: {
                          name: "coin",
                          color: "blue500",
                          size: "M"
                        }
                      },
                      {
                        description: `${
                          importPreview?.toBeCreatedPayables.length
                        } new ${payableStatus.toLowerCase()} ${pluralize(
                          importPreview?.toBeCreatedPayables.length,
                          payablesPluralizeOptions
                        )} will be created.`,
                        icon: {
                          name: "receipt",
                          color: "blue500",
                          size: "M"
                        }
                      },
                      {
                        description: `${
                          importPreview?.toBeUpdatedPayables.length
                        } existing ${pluralize(
                          importPreview?.toBeUpdatedPayables.length,
                          payablesPluralizeOptions
                        )} will be updated with imported line items.`,
                        icon: {
                          name: "fileEdit",
                          color: "blue500",
                          size: "M"
                        }
                      },
                      ...([PayableStatus.Open, PayableStatus.Approved].includes(
                        payableStatus
                      )
                        ? [
                            {
                              description: `${
                                importPreview?.statusUpdateCount
                              } existing ${pluralize(
                                importPreview?.statusUpdateCount,
                                payablesPluralizeOptions
                              )} will be ${payableStatus !==
                                PayableStatus.Draft &&
                                payableStatus !== PayableStatus.Paid &&
                                payableStatusLabelMap[payableStatus]}.`,
                              icon: {
                                name: "pencil",
                                color: "blue500",
                                size: "M"
                              }
                            } as IconListItem
                          ]
                        : []),
                      ...(importPreview?.negativeAmountCount > 0
                        ? [
                            {
                              description: `${
                                importPreview?.negativeAmountCount
                              } ${pluralize(
                                importPreview?.negativeAmountCount,
                                payablesPluralizeOptions
                              )} with a negative payment amount will be created as a draft.`,
                              icon: {
                                name: "warning",
                                color: "red500",
                                size: "M"
                              }
                            } as IconListItem
                          ]
                        : [])
                    ]}
                  />

                  <WSButton
                    icon="upload"
                    fullWidth
                    onClick={onConfirmAndImport}
                    loading={confirmAndImportMeta.isLoading}
                  >
                    Import Payables
                  </WSButton>
                </>
              );
            })()}
          </WSPanel>
        </WSGridCentered>
      </WSContainer>
    </ProjectOnboardingLayout>
  );
};
